/**
 * Typography Tokens
 * Font families, sizes, weights, and line heights for consistent typography.
 * Poppins for headings, Open Sans for body text.
 */

const primaryStack =
  '"Poppins", "SF Pro Display", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const bodyStack =
  '"Poppins", "SF Pro Text", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const ctaStack =
  '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
// Smaller-than-body text (eyebrows, captions, metadata) uses Roboto for
// better legibility at small sizes than Poppins, which thins out fast.
const smallStack =
  '"Roboto", "SF Pro Text", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

export const typography = {
  // Font families
  fontFamily: {
    heading: primaryStack,
    body: bodyStack,
    CTA: ctaStack,
    small: smallStack,
    mono:
      '"SF Mono", Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
  },

  // Font sizes (in px)
  fontSize: {
    xxs: '10px',
    xs: '12px',
    sm: '14px',
    base: '16px',
    md: '17px',
    lg: '20px',
    xl: '24px',
    '2xl': '28px',
    '3xl': '34px',
    '4xl': '44px',
    '5xl': '56px',
    '6xl': '72px',
  },

  // Font weights (legacy naming retained for existing usage)
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },

  // New weight tokens for future usage
  fontWeights: {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
  },

  // Line heights
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.6,
    loose: 2,
    headingTight: 1.1,
    bodyRelaxed: 1.6,
  },

  // Letter spacing
  letterSpacing: {
    tight: '-0.02em',
    normal: '0',
    wide: '0.02em',
  },
} as const;

/**
 * Responsive Type Ramp
 * Systematic responsive typography scale for consistent sizing across breakpoints.
 * Use these for common typographic patterns that need to scale across mobile/tablet/desktop.
 */
const responsiveTypeRamp = {
  // Lead paragraphs - subtitle/tagline text under headlines
  lead: {
    xs: '18px',                        // 18px – mobile (custom size for better line breaking)
    md: typography.fontSize.xl,        // 24px – tablet
    lg: typography.fontSize['2xl'],    // 28px – desktop
    lineHeight: typography.lineHeight.relaxed,
  },
  // Section titles - accordion headers, card titles
  sectionTitle: {
    xs: typography.fontSize.md,        // 17px – mobile
    md: typography.fontSize.lg,        // 20px – tablet
    lg: typography.fontSize.xl,        // 24px – desktop
    lineHeight: typography.lineHeight.normal,
  },
  // Large body copy - marketing copy, expanded accordion content
  bodyLarge: {
    xs: typography.fontSize.base,      // 16px – mobile
    md: typography.fontSize.lg,        // 20px – tablet
    lg: typography.fontSize['2xl'],    // 28px – desktop (for wide layouts)
    lineHeight: typography.lineHeight.bodyRelaxed,
  },
  // Detail text - supporting bullets, metadata, helper text
  detail: {
    xs: typography.fontSize.sm,        // 14px – mobile
    md: typography.fontSize.base,      // 16px – tablet
    lg: typography.fontSize.md,        // 17px – desktop
    lineHeight: typography.lineHeight.relaxed,
  },
} as const;

export const typeRamp = responsiveTypeRamp;

